<template>
  <teleport v-if="activeIndex !== -1" to="#modals">
    <div
      ref="draggable"
      class="fixed z-50 w-[520px] touch-none rounded-sm border-2 border-blue-200 select-none"
      :style="{
        top: `${restrictedY}px`,
        left: `${restrictedX}px`,
      }"
      data-cy="custom-attribute-preview-wrapper"
    >
      <custom-attributes-preview
        class="rounded-sm bg-white py-3 shadow-lg"
        :attributes="attributesList"
        :active-index="activeIndex"
        :loading="loading"
        @update:active="$emit('update:active', $event)"
      />
    </div>
  </teleport>
</template>
<script setup lang="ts">
import {
  clamp,
  useDraggable,
  useElementBounding,
  useWindowSize,
} from "@vueuse/core";
import { computed, ref, watch } from "vue";

import { storeToRefs } from "pinia";

import { usePromiseWrapper } from "@/hooks/common";
import attributeBuilderService from "@/services/modules/attributeBuilder";
import { useAttributeBuilderStore } from "@/stores/customAttributeBuilder";

import CustomAttributesPreview from "@/views/profile/components/dataOrchestration/configPanel/customAttributes/CustomAttributesPreview.vue";

import type { CustomAttribute } from "@/models/customAttributes/customAttribute";
import type { IAttribute } from "@/models/orchestration";

defineEmits<{
  "update:active": [index: number];
}>();

const props = withDefaults(
  defineProps<{
    activeIndex: number;
    attributes?: CustomAttribute[];
    attributesToPreview?: IAttribute[];
  }>(),
  {
    attributes: () => [],
    attributesToPreview: () => [],
  }
);

const attributeBuilderStore = useAttributeBuilderStore();
const { services } = storeToRefs(attributeBuilderStore);
const { getServices } = attributeBuilderStore;
if (!services.value.length) {
  getServices();
}

const attributesMap = ref<Record<CustomAttribute["id"], CustomAttribute>>({});
const draggable = ref<HTMLDivElement>();

const { width: windowWidth, height: windowHeight } = useWindowSize();
const { width, height } = useElementBounding(draggable);
const { x, y } = useDraggable(draggable, {
  initialValue: { x: windowWidth.value / 2, y: windowHeight.value / 2 },
  preventDefault: true,
  onStart: (_, event) => {
    const target = event.target as HTMLElement;
    const isInputElement = target.closest(
      'input, textarea, [data-cy="formula-editor"], .formula-editor'
    );
    if (isInputElement) {
      return false;
    }
  },
});

const restrictedX = computed(() =>
  clamp(0, x.value, windowWidth.value - width.value)
);
const restrictedY = computed(() =>
  clamp(0, y.value, windowHeight.value - height.value)
);

const attributesList = computed(() => {
  if (props.attributes.length) {
    return props.attributes;
  }
  return props.attributesToPreview.reduce<CustomAttribute[]>((acc, attr) => {
    if (attr.context?.value?.id) {
      acc.push(attributesMap.value[attr.context.value.id]);
    }
    return acc;
  }, []);
});

const { loading, fetchWrapper: getAttribute } = usePromiseWrapper(
  async (newVal) => {
    if (attributesList.value[newVal]) {
      return;
    }
    const attributeToFetch = props.attributesToPreview[props.activeIndex];
    if (!attributeToFetch?.context?.value?.id) {
      return;
    }
    const temporaryAttribute = {
      name: attributeToFetch.name,
      id: attributeToFetch.context.value.id,
    } as CustomAttribute;
    attributesMap.value[attributeToFetch.context.value.id] = temporaryAttribute;

    const attribute = await attributeBuilderService.getAttribute(
      attributeToFetch.context.value.id
    );
    attributesMap.value[attribute.id] = attribute;
  }
);

watch(() => props.activeIndex, getAttribute, { immediate: true });
</script>
